import React, { useMemo, useState } from 'react'
import { useQuery, gql } from '@apollo/client'
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Tabs,
  Tab,
  CircularProgress
} from '@mui/material'
import DataTable from 'react-data-table-component'
import SearchIcon from '@mui/icons-material/Search'
import RefreshIcon from '@mui/icons-material/Refresh'
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined'

// Requests left on the customer app's no-delivery home screen (migration 139):
// "Notify me when delivery starts" and "Suggest a restaurant in <city>".
const GET_DELIVERY_REQUESTS = gql`
  query DeliveryRequests {
    deliveryWaitlist(limit: 1000) {
      _id
      userName
      userEmail
      userPhone
      address
      city
      notifiedAt
      createdAt
    }
    restaurantSuggestions(limit: 1000) {
      _id
      userName
      userEmail
      userPhone
      restaurantName
      note
      city
      address
      createdAt
    }
  }
`

const when = (iso) => (iso ? new Date(iso).toLocaleString() : '—')
const cityOf = (r) => (r.city || '').trim() || 'Unknown'

function Contact({ row }) {
  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>{row.userName || '—'}</Typography>
      {row.userEmail && <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>{row.userEmail}</Typography>}
      {row.userPhone && <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>{row.userPhone}</Typography>}
    </Box>
  )
}

const tableStyles = {
  headCells: { style: { backgroundColor: '#f8fafc', fontWeight: 700, fontSize: 12, color: '#475569', textTransform: 'uppercase' } },
  rows: { style: { minHeight: 64, '&:hover': { backgroundColor: '#f8fafc' } } }
}

export default function DeliveryRequests() {
  const [tab, setTab] = useState(0)
  const [search, setSearch] = useState('')
  const { data, loading, refetch } = useQuery(GET_DELIVERY_REQUESTS, { fetchPolicy: 'network-only' })

  const waitlist = useMemo(() => data?.deliveryWaitlist ?? [], [data])
  const suggestions = useMemo(() => data?.restaurantSuggestions ?? [], [data])

  // Where demand is: one row per city, most requested first.
  const byCity = useMemo(() => {
    const map = new Map()
    const bump = (city, key) => {
      const row = map.get(city) || { city, waitlist: 0, suggestions: 0 }
      row[key] += 1
      map.set(city, row)
    }
    waitlist.forEach((w) => bump(cityOf(w), 'waitlist'))
    suggestions.forEach((s) => bump(cityOf(s), 'suggestions'))
    return [...map.values()].sort((a, b) => b.waitlist + b.suggestions - (a.waitlist + a.suggestions))
  }, [waitlist, suggestions])

  const q = search.trim().toLowerCase()
  const matches = (...vals) => !q || vals.some((v) => v && String(v).toLowerCase().includes(q))
  const shownWaitlist = waitlist.filter((w) => matches(w.city, w.address, w.userName, w.userEmail, w.userPhone))
  const shownSuggestions = suggestions.filter((s) => matches(s.restaurantName, s.city, s.address, s.note, s.userName, s.userEmail))
  const shownCities = byCity.filter((c) => matches(c.city))

  const waitlistColumns = [
    { name: 'Requested', selector: (r) => r.createdAt, sortable: true, cell: (r) => <Typography variant="caption" sx={{ color: '#64748b' }}>{when(r.createdAt)}</Typography>, width: '170px' },
    { name: 'Customer', cell: (r) => <Contact row={r} />, grow: 1.2 },
    { name: 'City', selector: (r) => cityOf(r), sortable: true, cell: (r) => <Typography variant="body2" sx={{ fontWeight: 600 }}>{cityOf(r)}</Typography>, width: '150px' },
    { name: 'Address', hide: 'md', cell: (r) => <Typography variant="body2" sx={{ color: '#475569' }}>{r.address || '—'}</Typography>, grow: 2 },
    {
      name: 'Status',
      cell: (r) => (
        <Chip
          size="small"
          label={r.notifiedAt ? `Notified ${new Date(r.notifiedAt).toLocaleDateString()}` : 'Waiting'}
          sx={{ bgcolor: r.notifiedAt ? '#dcfce7' : '#fef3c7', color: r.notifiedAt ? '#166534' : '#92400e', fontWeight: 600, fontSize: 11 }}
        />
      ),
      width: '150px'
    }
  ]

  const suggestionColumns = [
    { name: 'Suggested', selector: (r) => r.createdAt, sortable: true, cell: (r) => <Typography variant="caption" sx={{ color: '#64748b' }}>{when(r.createdAt)}</Typography>, width: '170px' },
    { name: 'Restaurant', selector: (r) => r.restaurantName, sortable: true, cell: (r) => <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>{r.restaurantName}</Typography>, grow: 1.2 },
    { name: 'City', selector: (r) => cityOf(r), sortable: true, cell: (r) => <Typography variant="body2" sx={{ fontWeight: 600 }}>{cityOf(r)}</Typography>, width: '150px' },
    { name: 'Note', hide: 'md', cell: (r) => <Typography variant="body2" sx={{ color: '#475569' }}>{r.note || '—'}</Typography>, grow: 2 },
    { name: 'From', hide: 'sm', cell: (r) => <Contact row={r} />, grow: 1.2 }
  ]

  const cityColumns = [
    { name: 'City', selector: (r) => r.city, sortable: true, cell: (r) => <Typography variant="body2" sx={{ fontWeight: 700 }}>{r.city}</Typography>, grow: 2 },
    { name: 'Waiting for delivery', selector: (r) => r.waitlist, sortable: true, right: true },
    { name: 'Restaurant suggestions', selector: (r) => r.suggestions, sortable: true, right: true }
  ]

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', py: 3 }}>
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <NotificationsActiveOutlinedIcon sx={{ color: '#14532d', fontSize: 28 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>Delivery Requests</Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Customers at addresses with no delivery: who wants to be notified, and which restaurants they want added
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => refetch()} sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}>
            <RefreshIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
          {[
            { label: 'Waiting for delivery', value: waitlist.filter((w) => !w.notifiedAt).length, color: '#14532d' },
            { label: 'Restaurant suggestions', value: suggestions.length, color: '#ea580c' },
            { label: 'Most requested city', value: byCity[0]?.city || '—', color: '#1e293b' }
          ].map((s) => (
            <Paper key={s.label} sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: s.color }}>{s.value}</Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>{s.label}</Typography>
            </Paper>
          ))}
        </Box>

        <Paper sx={{ borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: 'none' }}>
          <Box sx={{ px: 2, pt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, borderBottom: '1px solid #f1f5f9' }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 700 } }}>
              <Tab label={`By city (${byCity.length})`} />
              <Tab label={`Waitlist (${waitlist.length})`} />
              <Tab label={`Suggestions (${suggestions.length})`} />
            </Tabs>
            <TextField
              placeholder="Search city, customer, restaurant…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              sx={{ width: 320, my: 1 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment> }}
            />
          </Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: '#14532d' }} />
            </Box>
          ) : (
            <DataTable
              columns={tab === 0 ? cityColumns : tab === 1 ? waitlistColumns : suggestionColumns}
              data={tab === 0 ? shownCities : tab === 1 ? shownWaitlist : shownSuggestions}
              keyField={tab === 0 ? 'city' : '_id'}
              pagination
              paginationPerPage={25}
              customStyles={tableStyles}
              noDataComponent={
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography sx={{ color: '#94a3b8' }}>
                    {tab === 2
                      ? 'No restaurant suggestions yet.'
                      : 'No requests yet. They appear when customers at an address with no delivery tap "Notify me".'}
                  </Typography>
                </Box>
              }
            />
          )}
        </Paper>
      </Container>
    </Box>
  )
}
